import React from 'react';
import RunReportButton from './run-report-button';
import RunningReportButton from './running-report-button';
import { PermissionItem, ReportStatus } from '../../../../types';
import {
  isAbleToSeeUIElement,
  ToolsRoles,
} from '../../../utils/roles/client-roles';
import { useUserSettings } from '../../../utils/use-user-settings';
import { Messages } from '../../../utils/message-constants';

import IconButton from '@mui/material/IconButton';
import Stack from '@mui/material/Stack';
import Tooltip from '@mui/material/Tooltip';
import Edit from '@mui/icons-material/Edit';
import PushPinIcon from '@mui/icons-material/PushPin';

interface ReportActionsCellProps {
  // Permission
  rolePermission?: PermissionItem;

  // Edit
  hasEditPermission: boolean;
  editTooltip: string;
  onEdit: (e: React.MouseEvent) => void;

  // Run / Running
  reportStatus: ReportStatus;
  canRunReport: boolean;
  cannotRunReason: string | undefined;
  onRun: (e: React.MouseEvent) => void;

  // Pin
  isPinned: boolean;
  isDomainPinned: boolean;
  isPersonalPinned: boolean;
  isSavingPin: boolean;
  onPin: (e: React.MouseEvent) => void;
}

export default function ReportActionsCell({
  rolePermission,
  hasEditPermission,
  editTooltip,
  onEdit,
  reportStatus,
  canRunReport,
  cannotRunReason,
  onRun,
  isPinned,
  isDomainPinned,
  isPersonalPinned,
  isSavingPin,
  onPin,
}: Readonly<ReportActionsCellProps>) {
  const { toolsRoles } = useUserSettings();
  const pinColor = isDomainPinned
    ? ('info' as const)
    : isPersonalPinned
    ? ('primary' as const)
    : ('action' as const);
  const hasRunAllReportsPermission = isAbleToSeeUIElement(
    toolsRoles,
    ToolsRoles.MaunalRunReport.value
  );
  const isRolePermissionEnabled =
    hasRunAllReportsPermission ||
    !rolePermission ||
    isAbleToSeeUIElement(toolsRoles, rolePermission.value);

  const runningButton = (() => {
    if (reportStatus === 'RUNNING') {
      return <RunningReportButton onClick={(e) => e.stopPropagation()} />;
    }

    // IDLE or COMPLETED — show the Run button
    return (
      <Tooltip
        arrow
        title={
          !isRolePermissionEnabled
            ? rolePermission
              ? Messages.Errors.Permission.Acting(rolePermission?.name)
              : ''
            : cannotRunReason ?? ''
        }
      >
        <span style={{ display: 'inline-flex', pointerEvents: 'auto' }}>
          <RunReportButton
            disabled={!canRunReport || !isRolePermissionEnabled}
            onClick={(e) => {
              e.stopPropagation();
              onRun(e);
            }}
          />
        </span>
      </Tooltip>
    );
  })();

  return (
    <Stack
      direction="row"
      alignItems="center"
      justifyContent="flex-end"
      gap={0.5}
      sx={{ width: '100%' }}
    >
      {runningButton}

      <Tooltip arrow title={editTooltip}>
        <span style={{ display: 'inline-flex' }}>
          <IconButton
            size="small"
            disabled={!hasEditPermission}
            onClick={(e) => {
              e.stopPropagation();
              onEdit(e);
            }}
          >
            <Edit fontSize="small" />
          </IconButton>
        </span>
      </Tooltip>

      <Tooltip arrow title={isPinned ? 'Unpin' : 'Pin'}>
        <span style={{ display: 'inline-flex' }}>
          <IconButton
            size="small"
            disabled={isSavingPin}
            onClick={(e) => {
              e.stopPropagation();
              onPin(e);
            }}
          >
            <PushPinIcon fontSize="small" color={pinColor} />
          </IconButton>
        </span>
      </Tooltip>
    </Stack>
  );
}
